/**
 * Runtime Environment Configuration
 *
 * Provides centralized access to environment variables injected at runtime
 * via window._env_ (generated by env.sh at container startup).
 *
 * Priority: window._env_ (runtime) > process.env (build-time) > defaults
 */

interface EnvConfig {
  REACT_APP_OWAY_REST_URL_BASE: string;
  REACT_APP_POSTHOG_TOKEN: string;
  REACT_APP_POSTHOG_HOST: string;
  REACT_APP_ENVIRONMENT: string;
}

declare global {
  interface Window {
    _env_?: Partial<EnvConfig>;
  }
}

function getConfig(): EnvConfig {
  return {
    REACT_APP_OWAY_REST_URL_BASE:
      window._env_?.REACT_APP_OWAY_REST_URL_BASE ??
      process.env.REACT_APP_OWAY_REST_URL_BASE ??
      "",

    REACT_APP_POSTHOG_TOKEN:
      window._env_?.REACT_APP_POSTHOG_TOKEN ??
      process.env.REACT_APP_POSTHOG_TOKEN ??
      "",

    REACT_APP_POSTHOG_HOST:
      window._env_?.REACT_APP_POSTHOG_HOST ??
      process.env.REACT_APP_POSTHOG_HOST ??
      "",

    REACT_APP_ENVIRONMENT:
      window._env_?.REACT_APP_ENVIRONMENT ??
      process.env.REACT_APP_ENVIRONMENT ??
      process.env.NODE_ENV ??
      "production",
  };
}

// Proxy defers evaluation until access time, ensuring window._env_ is loaded
export const config = new Proxy({} as EnvConfig, {
  get: (_, prop: keyof EnvConfig) => {
    return getConfig()[prop];
  },
});

/**
 * Derive a URL by replacing the current subdomain.
 * e.g. react.oway.io -> api.oway.io
 */
function deriveUrlFromHostname(
  targetSubdomain: string,
  localhostPort?: string,
): string {
  const hostname = window.location.hostname;

  if (hostname === "localhost") {
    return localhostPort
      ? `http://localhost:${localhostPort}`
      : "http://localhost:3000";
  }

  const dotIndex = hostname.indexOf(".");
  if (dotIndex === -1) {
    console.warn("Cannot derive URL from single-level hostname:", hostname);
    return window.location.origin;
  }

  const newHostname = hostname.replace(/^[^.]+/, targetSubdomain);
  return `https://${newHostname}`;
}

export function getEnvironmentRestUrl(): string {
  // never the build-time value: it bakes localhost into prod (OWA-2411)
  const runtimeBase = window._env_?.REACT_APP_OWAY_REST_URL_BASE;
  if (runtimeBase) {
    return runtimeBase;
  }

  return deriveUrlFromHostname("api", "8180");
}
